import React, { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { IFormField } from '../../types/suggestions/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Returns true if the placeholder looks like an input mask, e.g. "(xxx) xxx-xxxx" */
function isMaskPattern(placeholder?: string): boolean {
    if (!placeholder) return false;
    return /x/.test(placeholder) && /[()/\- ]/.test(placeholder);
}

/** True if the field's regex has an open-ended quantifier (e.g. {4,}), meaning the
 *  placeholder's last digit group is just an example and shouldn't cap input length. */
function hasUnboundedRegex(field: IFormField): boolean {
    const regex = (field as any).regex;
    if (!regex || !Array.isArray(regex) || regex.length === 0) return false;
    return /\{\d+,\}/.test(regex[0]);
}

/** Extracts the user-typed characters from a masked display value */
function getRawFromMasked(masked: string, mask: string): string {
    const literals = new Set(mask.split('').filter(ch => ch !== 'x'));
    return masked.split('').filter(ch => !literals.has(ch)).join('');
}

/** Splits a mask like "x-xx-xxxxx" into each digit group's length and the literal
 *  separators between groups, e.g. groupLengths [1,2,5], separators ['-','-']. */
function parseMaskGroups(mask: string): { groupLengths: number[]; separators: string[] } {
    const groups = mask.match(/x+/g) || [];
    const separators = mask.split(/x+/).slice(1, -1);
    return { groupLengths: groups.map(g => g.length), separators };
}

/**
 * Applies a mask to raw user characters, auto-inserting literal separators between
 * digit groups. When openEndedLast is true, the final digit group has no upper bound
 * (used for fields like certification numbers whose regex allows unlimited trailing digits).
 */
function applyMaskToRaw(raw: string, mask: string, openEndedLast = false): string {
    const { groupLengths, separators } = parseMaskGroups(mask);
    let result = '';
    let idx = 0;
    for (let i = 0; i < groupLengths.length && idx < raw.length; i++) {
        const isLast = i === groupLengths.length - 1;
        const take = isLast && openEndedLast ? raw.length - idx : Math.min(groupLengths[i], raw.length - idx);
        result += raw.slice(idx, idx + take);
        idx += take;
        const groupFilled = take === groupLengths[i];
        if (!isLast && groupFilled && i < separators.length) {
            result += separators[i];
        }
    }
    return result;
}

interface FormFieldProps {
    field: IFormField;
    value: string | number | Date | { results: string[] };
    onChange: (value: any) => void;
    error: boolean
}

export const FormField: React.FC<FormFieldProps> = ({ field, value, onChange, error }) => {
    const [dateOpen, setDateOpen] = useState(false);
    const [dateText, setDateText] = useState<string>(() => {
        if (value instanceof Date) {
            const mm = String(value.getMonth() + 1).padStart(2, '0');
            const dd = String(value.getDate()).padStart(2, '0');
            return `${mm}/${dd}/${value.getFullYear()}`;
        }
        return '';
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (field.type === 'Text' && (field as any).digitonly && newValue) {
            onChange(newValue.replace(/[^0-9-]/g, ''));
        } else {
            onChange(newValue || '');
        }
    };

    const renderField = () => {
        switch (field.type) {
            case 'Text': {
                const mask = isMaskPattern(field.placeholder) ? field.placeholder! : null;
                const openEnded = hasUnboundedRegex(field);
                const handleMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {

                    const newInput = e.target.value;
                    const prevMasked = (value as string) || '';
                    const isDeleting = newInput.length < prevMasked.length;

                    let raw = getRawFromMasked(newInput, mask!);
                    if (isDeleting) {
                        const prevRaw = getRawFromMasked(prevMasked, mask!);
                        // User deleted a literal char — remove the preceding user char instead
                        if (raw.length === prevRaw.length && raw.length > 0) {
                            raw = raw.slice(0, -1);
                        }
                    }

                    onChange(applyMaskToRaw(raw, mask!, openEnded));
                };

                return (
                    <>
                        <Input
                            value={value as string}
                            onChange={mask ? handleMaskedChange : handleInputChange}
                            placeholder={field.placeholder}
                            maxLength={mask ? (openEnded ? undefined : mask.length) : field.maxlength}
                            className={error ? 'border-destructive' : ''}
                        />
                        {error && field.error && (
                            <p className="text-sm text-destructive mt-1">{field.error}</p>
                        )}
                    </>
                );
            }

            case 'Number':
                return (
                    <>
                        <Input
                            value={value?.toString() || ''}
                            onChange={handleInputChange}
                            type="number"
                            placeholder={field.placeholder}
                            className={error ? 'border-destructive' : ''}
                        />
                        {error && field.error && (
                            <p className="text-sm text-destructive mt-1">{field.error}</p>
                        )}
                    </>
                );

            case 'Date': {
                const today = new Date();
                let minDate: Date | undefined;
                let maxDate: Date | undefined;

                if (field.mindays) {
                    minDate = new Date();
                    minDate.setDate(today.getDate() + parseInt(field.mindays));
                }

                if (field.maxdays) {
                    maxDate = new Date();
                    maxDate.setDate(today.getDate() + parseInt(field.maxdays));
                }

                const selectedDate = value instanceof Date ? value : undefined;

                const formatDateForInput = (date: Date) => {
                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                    const dd = String(date.getDate()).padStart(2, '0');
                    const yyyy = date.getFullYear();
                    return `${mm}/${dd}/${yyyy}`;
                };

                const dateMask = 'xx/xx/xxxx';

                const handleDateTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                    const newInput = e.target.value;
                    const isDeleting = newInput.length < dateText.length;
                    let raw = getRawFromMasked(newInput, dateMask);
                    if (isDeleting) {
                        const prevRaw = getRawFromMasked(dateText, dateMask);
                        if (raw.length === prevRaw.length && raw.length > 0) {
                            raw = raw.slice(0, -1);
                        }
                    }
                    setDateText(applyMaskToRaw(raw, dateMask));
                };

                const handleDateTextBlur = () => {
                    if (dateText === '') {
                        onChange('');
                        return;
                    }
                    const parsed = new Date(dateText);
                    if (!isNaN(parsed.getTime())) {
                        const isDisabled =
                            (minDate && parsed < minDate) ||
                            (maxDate && parsed > maxDate);
                        if (!isDisabled) {
                            onChange(parsed);
                            setDateText(formatDateForInput(parsed));
                        } else {
                            // Out of range — revert to last valid value
                            setDateText(selectedDate ? formatDateForInput(selectedDate) : '');
                        }
                    } else {
                        // Invalid date — revert to last valid value
                        setDateText(selectedDate ? formatDateForInput(selectedDate) : '');
                    }
                };

                return (
                    <>
                        <div className="flex gap-1">
                            <Input
                                value={dateText}
                                onChange={handleDateTextChange}
                                onBlur={handleDateTextBlur}
                                placeholder={field.placeholder || 'MM/DD/YYYY'}
                                maxLength={dateMask.length}
                                className={cn('flex-1', error && 'border-destructive')}
                            />
                            <Popover open={dateOpen} onOpenChange={setDateOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="icon">
                                        <CalendarIcon className="h-4 w-4" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="end">
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={(date) => {
                                            onChange(date || '');
                                            setDateText(date ? formatDateForInput(date) : '');
                                            setDateOpen(false);
                                        }}
                                        disabled={(date) => {
                                            if (minDate && date < minDate) return true;
                                            if (maxDate && date > maxDate) return true;
                                            return false;
                                        }}
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        {error && field.error && (
                            <p className="text-sm text-destructive mt-1">{field.error}</p>
                        )}
                    </>
                );
            }

            case 'Choice': {
                const options = field.options || [];
                return (
                    <>
                        <Select
                            value={value as string}
                            onValueChange={(val) => onChange(val)}
                        >
                            <SelectTrigger className={error ? 'border-destructive' : ''}>
                                <SelectValue placeholder={field.placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                {options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {error && field.error && (
                            <p className="text-sm text-destructive mt-1">{field.error}</p>
                        )}
                    </>
                );
            }

            case 'List': {
                const options = field.options || [];
                return (
                    <>
                        <Select
                            value={value as string}
                            onValueChange={(val) => onChange(val)}
                        >
                            <SelectTrigger className={error ? 'border-destructive' : ''}>
                                <SelectValue placeholder={field.placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                                {options.map((option) => (
                                    <SelectItem key={option.key} value={option.key}>
                                        {option.text}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {error && field.error && (
                            <p className="text-sm text-destructive mt-1">{field.error}</p>
                        )}
                    </>
                );
            }

            case 'Internal':
            default:
                return null;
        }
    };

    return (
        <div className="mb-4">
            <Label className="font-semibold mb-1 block">
                {field.title} {field.required && <span className="text-destructive">*</span>}
            </Label>
            {renderField()}
            {!error && field.description && (
                <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
        </div>
    );
};

export default FormField;
